import { Button, Field } from '@freightapi/ui'
import { createFileRoute, Link } from '@tanstack/react-router'
import { type FormEvent, useState } from 'react'
import { ApiError, requestMagicLink } from '../lib/api'
import { useConsoleI18n } from '../lib/locale'

/**
 * Login for an address that already exists. There is no password anywhere in this product: the
 * only credential is a link in the developer's inbox, valid for fifteen minutes (ADR 0005).
 */
interface LoginSearch {
  /** Set by better-auth when a magic link could not be redeemed; see `login.error.unknown_email`. */
  readonly error?: string
}

export const Route = createFileRoute('/login')({
  component: Login,
  validateSearch: (search: Record<string, unknown>): LoginSearch =>
    typeof search.error === 'string' ? { error: search.error } : {},
})

function Login() {
  const { t, locale } = useConsoleI18n()
  // better-auth sends the browser back here when a magic link cannot mint a session — an address
  // that has no account and no invitation, above all (M3-02).
  const { error: rejected } = Route.useSearch()
  const [email, setEmail] = useState('')
  const [state, setState] = useState<'idle' | 'sending' | 'sent'>('idle')
  const [error, setError] = useState<string | undefined>(undefined)

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setState('sending')
    setError(undefined)
    try {
      await requestMagicLink({ email, locale })
      setState('sent')
    } catch (cause) {
      setState('idle')
      setError(t(cause instanceof ApiError ? cause.messageKey : 'error.unexpected'))
    }
  }

  if (state === 'sent') {
    return (
      <section className="mx-auto flex max-w-md flex-col gap-3">
        <h1 className="font-semibold text-display text-ink">{t('login.sent.title')}</h1>
        <p className="text-body text-muted">{t('login.sent.body', { email })}</p>
      </section>
    )
  }

  return (
    <section className="mx-auto flex max-w-md flex-col gap-5">
      <h1 className="font-semibold text-display text-ink">{t('login.title')}</h1>
      <p className="text-body text-muted">{t('login.intro')}</p>
      {rejected === undefined ? null : (
        <p role="alert" className="text-body text-danger">
          {t('login.error.unknown_email')}
        </p>
      )}

      <form className="flex flex-col gap-4" onSubmit={(event) => void onSubmit(event)}>
        <Field label={t('login.email.label')} required error={error}>
          {({ id, describedBy, invalid }) => (
            <input
              id={id}
              aria-describedby={describedBy}
              aria-invalid={invalid || undefined}
              type="email"
              name="email"
              autoComplete="email"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              className="min-h-touch rounded-card border border-line bg-surface px-3 text-body text-ink"
            />
          )}
        </Field>
        <Button type="submit" block busy={state === 'sending'} busyLabel={t('loading')}>
          {t('login.submit')}
        </Button>
      </form>

      <Link to="/signup" className="text-body text-brand-700 underline">
        {t('login.to_signup')}
      </Link>
    </section>
  )
}
